import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import agentsSidecarAPI from 'dashboard/api/agentsSidecar';

export function useAgentsSidecar(conversation, messages) {
  const { t } = useI18n();

  const isLoading = ref(false);
  const isThreadLoading = ref(false);
  const isAnalyzing = ref(false);
  const isQuerying = ref(false);
  const error = ref(null);
  const currentStep = ref('');
  const progress = ref(0);

  const summary = ref(null);
  const draftReply = ref(null);
  const suggestions = ref({
    tags: [],
    actions: [],
    macros: [],
    cannedResponses: [],
  });
  const escalation = ref(null);

  const queryAnswer = ref(null);
  const querySources = ref([]);
  const chatHistory = ref([]);
  const currentQueryText = ref('');

  const currentThreadId = ref(null);
  const threads = ref([]);

  const chatContainerRef = ref(null);

  const getStorageKey = conversationId =>
    `agents_sidecar_threads_${conversationId}`;

  const saveThreads = () => {
    const convId = conversation.value?.id;
    if (!convId) return;

    try {
      localStorage.setItem(
        getStorageKey(convId),
        JSON.stringify({
          threads: threads.value,
          currentThreadId: currentThreadId.value,
        })
      );
    } catch {
      // localStorage error
    }
  };

  const loadThreadHistory = async threadId => {
    if (!threadId) return;

    try {
      const response = await agentsSidecarAPI.getThreadHistory(threadId);
      chatHistory.value = response.messages.map((msg, index) => ({
        id: `${threadId}-${index}`,
        query: msg.role === 'user' ? msg.content : null,
        answer: msg.role === 'assistant' ? msg.content : null,
        role: msg.role,
        timestamp: new Date(),
      }));

      const formatted = [];
      for (let i = 0; i < chatHistory.value.length; i += 2) {
        const userMsg = chatHistory.value[i];
        const assistantMsg = chatHistory.value[i + 1];
        if (userMsg && assistantMsg) {
          formatted.push({
            id: `${threadId}-${i}`,
            query: userMsg.query,
            answer: assistantMsg.answer,
            timestamp: new Date(),
          });
        }
      }
      chatHistory.value = formatted;
    } catch {
      chatHistory.value = [];
    }
  };

  const loadThreads = async () => {
    const convId = conversation.value?.id;
    if (!convId) return;

    isThreadLoading.value = true;
    let stored = null;
    try {
      const raw = localStorage.getItem(getStorageKey(convId));
      stored = raw ? JSON.parse(raw) : null;
    } catch {
      stored = null;
    }

    try {
      const response = await agentsSidecarAPI.listThreads(convId);
      threads.value = (response.threads || []).map(thread => ({
        id: thread.thread_id,
        title: thread.title,
        createdAt: thread.created_at ? new Date(thread.created_at) : new Date(),
        messageCount: thread.message_count || 0,
      }));
    } catch {
      threads.value = stored?.threads || [];
    }

    // Try to restore last active thread from storage, or fallback to first available
    if (
      stored?.currentThreadId &&
      threads.value.some(thread => thread.id === stored.currentThreadId)
    ) {
      currentThreadId.value = stored.currentThreadId;
    } else {
      currentThreadId.value = threads.value[0]?.id || null;
    }

    if (currentThreadId.value) {
      await loadThreadHistory(currentThreadId.value);
    }

    saveThreads();
    isThreadLoading.value = false;
  };

  const createNewThread = async () => {
    try {
      const convId = conversation.value?.id;
      const response = await agentsSidecarAPI.createThread(
        convId,
        `Chat ${threads.value.length + 1}`
      );

      currentThreadId.value = response.thread_id;
      threads.value.push({
        id: response.thread_id,
        title: response.title || `Chat ${threads.value.length + 1}`,
        createdAt: new Date(),
      });
      chatHistory.value = [];
      queryAnswer.value = null;
      querySources.value = [];
      saveThreads();

      return response.thread_id;
    } catch (err) {
      error.value = {
        code: 'thread_create_failed',
        message: err.message,
      };
      return null;
    }
  };

  const switchThread = async threadId => {
    currentThreadId.value = threadId;
    chatHistory.value = [];
    queryAnswer.value = null;
    querySources.value = [];
    await loadThreadHistory(threadId);
    saveThreads();
  };

  const deleteThread = threadId => {
    threads.value = threads.value.filter(thread => thread.id !== threadId);
    if (currentThreadId.value === threadId) {
      currentThreadId.value = threads.value[0]?.id || null;
      if (currentThreadId.value) {
        loadThreadHistory(currentThreadId.value);
      } else {
        chatHistory.value = [];
      }
    }
    saveThreads();
  };

  let abortController = null;

  const scrollToBottom = async () => {
    await nextTick();
    if (chatContainerRef.value) {
      chatContainerRef.value.scrollTop = chatContainerRef.value.scrollHeight;
    }
  };

  const reset = () => {
    isLoading.value = false;
    isAnalyzing.value = false;
    isQuerying.value = false;
    error.value = null;
    currentStep.value = '';
    progress.value = 0;
    summary.value = null;
    draftReply.value = null;
    suggestions.value = {
      tags: [],
      actions: [],
      macros: [],
      cannedResponses: [],
    };
    escalation.value = null;
    queryAnswer.value = null;
    querySources.value = [];
  };

  const cancel = () => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    isLoading.value = false;
    isAnalyzing.value = false;
    isQuerying.value = false;
  };

  const processAnalysisEvent = ({ event, data }) => {
    switch (event) {
      case 'status':
        currentStep.value = data.step;
        progress.value = data.progress || 0;
        break;

      case 'summary':
        summary.value = {
          text: data.text,
          intent: data.intent,
          sentiment: data.sentiment,
          keyPoints: data.key_points || [],
        };
        break;

      case 'draft_reply':
        draftReply.value = {
          text: data.text,
          confidence: data.confidence,
          tone: data.tone,
          alternatives: data.alternatives || [],
        };
        break;

      case 'suggestions':
        suggestions.value = {
          tags: data.tags || [],
          actions: data.actions || [],
          macros: data.macros || [],
          cannedResponses: data.canned_responses || [],
        };
        break;

      case 'escalation':
        escalation.value = {
          reason: data.reason,
          confidence: data.confidence,
          suggestedTeam: data.suggested_team,
          suggestedAssignee: data.suggested_assignee,
          priority: data.priority,
        };
        break;

      case 'error':
        error.value = {
          code: data.code,
          message: data.message,
        };
        break;

      case 'complete':
        isAnalyzing.value = false;
        isLoading.value = false;
        progress.value = 1;
        break;

      default:
        break;
    }
  };

  const processQueryEvent = ({ event, data }) => {
    switch (event) {
      case 'thinking':
        currentStep.value = data.step;
        break;

      case 'partial_answer':
        queryAnswer.value = {
          text: data.text,
          isPartial: !data.is_final,
        };
        scrollToBottom();
        break;

      case 'answer':
        if (data.thread_id) {
          currentThreadId.value = data.thread_id;

          const existingThread = threads.value.find(
            thread => thread.id === data.thread_id
          );
          if (!existingThread) {
            threads.value.push({
              id: data.thread_id,
              title: `Chat ${threads.value.length + 1}`,
              createdAt: new Date(),
            });
          }
          saveThreads();
        }

        queryAnswer.value = {
          text: data.text,
          confidence: data.confidence,
          isPartial: false,
        };
        querySources.value = data.sources || [];
        chatHistory.value.push({
          id: Date.now(),
          query: currentQueryText.value,
          answer: data.text,
          confidence: data.confidence,
          sources: data.sources || [],
          timestamp: new Date(),
        });
        scrollToBottom();
        break;

      case 'error':
        error.value = {
          code: data.code,
          message: data.message,
        };
        break;

      case 'complete':
        isQuerying.value = false;
        isLoading.value = false;
        break;

      default:
        break;
    }
  };

  const analyze = async (requestType = 'full_analysis') => {
    if (!conversation.value?.id) {
      error.value = { message: t('AGENTS_SIDECAR.ERRORS.NO_CONVERSATION') };
      return;
    }

    if (error.value?.message === t('AGENTS_SIDECAR.ERRORS.NO_CONVERSATION')) {
      error.value = null;
    }

    cancel();
    reset();
    isLoading.value = true;
    isAnalyzing.value = true;

    try {
      const stream = agentsSidecarAPI.analyzeStream(
        conversation.value.id,
        messages.value || [],
        requestType
      );

      // eslint-disable-next-line no-restricted-syntax
      for await (const event of stream) {
        processAnalysisEvent(event);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        error.value = {
          code: 'analysis_failed',
          message: err.message || t('AGENTS_SIDECAR.ERRORS.ANALYSIS_FAILED'),
        };
      }
    } finally {
      isLoading.value = false;
      isAnalyzing.value = false;
    }
  };

  const query = async queryText => {
    if (!queryText?.trim()) {
      error.value = { message: t('AGENTS_SIDECAR.ERRORS.EMPTY_QUERY') };
      return;
    }

    cancel();
    error.value = null;
    queryAnswer.value = null;
    querySources.value = [];
    currentQueryText.value = queryText.trim();
    isLoading.value = true;
    isQuerying.value = true;

    chatHistory.value.push({
      id: Date.now(),
      query: currentQueryText.value,
      answer: null,
      timestamp: new Date(),
      isPending: true,
    });
    scrollToBottom();

    try {
      const stream = agentsSidecarAPI.queryStream(
        queryText,
        conversation.value?.inbox_id,
        conversation.value?.id,
        messages.value || [],
        currentThreadId.value
      );

      // eslint-disable-next-line no-restricted-syntax
      for await (const event of stream) {
        processQueryEvent(event);
      }

      chatHistory.value = chatHistory.value.filter(h => !h.isPending);
    } catch (err) {
      chatHistory.value = chatHistory.value.filter(h => !h.isPending);
      if (err.name !== 'AbortError') {
        error.value = {
          code: 'query_failed',
          message: err.message || t('AGENTS_SIDECAR.ERRORS.QUERY_FAILED'),
        };
      }
    } finally {
      isLoading.value = false;
      isQuerying.value = false;
    }
  };

  const useDraftReply = () => {
    if (draftReply.value?.text) {
      return draftReply.value.text;
    }
    return null;
  };

  const hasResults = computed(() => {
    return !!(
      summary.value ||
      draftReply.value ||
      suggestions.value.tags.length ||
      suggestions.value.actions.length ||
      escalation.value
    );
  });

  const hasQueryAnswer = computed(() => {
    return !!queryAnswer.value?.text;
  });

  const hasChatHistory = computed(() => {
    return chatHistory.value.length > 0;
  });

  const hasThreads = computed(() => {
    return threads.value.length > 0;
  });

  const clearChatHistory = () => {
    chatHistory.value = [];
    queryAnswer.value = null;
    querySources.value = [];
    currentQueryText.value = '';
    currentThreadId.value = null;
    threads.value = [];
    const convId = conversation.value?.id;
    if (convId) {
      try {
        localStorage.removeItem(getStorageKey(convId));
      } catch {
        // empty
      }
    }
  };

  watch(
    () => conversation.value?.id,
    newId => {
      if (newId) {
        queryAnswer.value = null;
        querySources.value = [];
        currentQueryText.value = '';
        chatHistory.value = [];
        currentThreadId.value = null;
        threads.value = [];
        loadThreads();
      }
    },
    { immediate: true }
  );

  onUnmounted(() => {
    cancel();
  });

  return {
    isLoading,
    isThreadLoading,
    isAnalyzing,
    isQuerying,
    error,
    currentStep,
    progress,

    summary,
    draftReply,
    suggestions,
    escalation,

    queryAnswer,
    querySources,
    chatHistory,

    currentThreadId,
    threads,
    chatContainerRef,

    hasResults,
    hasQueryAnswer,
    hasChatHistory,
    hasThreads,

    analyze,
    query,
    useDraftReply,
    reset,
    cancel,
    clearChatHistory,
    createNewThread,
    switchThread,
    deleteThread,
    scrollToBottom,
  };
}
